import { useEffect, useRef, useState } from 'react';
import { Mic, MicOff, Phone, PhoneOff, Loader2, X } from 'lucide-react';
import { useVoiceChat, VOICE_STATUS } from '@hooks/useVoiceChat';

const EXPLAINER_SEEN_KEY = 'draw-lab-voice-explainer-seen';

/**
 * VoicePanel — the voice chat UI for DrawLab.
 *
 * Uses a "drop-in voice channel" model (like Discord or Clubhouse):
 * - Tap the phone to join the room's voice channel
 * - Hear everyone else who has also joined
 * - No ringing, no acceptance — just join when you want to talk
 *
 * On first encounter, shows a small explainer popover so users
 * understand the model without having to read docs.
 *
 * Audio playback is handled via hidden <audio> elements per peer.
 */
export default function VoicePanel() {
  const { status, peers, remoteStreams, isMuted, error, joinVoice, leaveVoice, toggleMute, isActive } = useVoiceChat();
  const [showExplainer, setShowExplainer] = useState(false);
  const [hasSeenExplainer, setHasSeenExplainer] = useState(() => {
    try { return localStorage.getItem(EXPLAINER_SEEN_KEY) === '1'; } catch { return false; }
  });

  // Auto-show the explainer ~800ms after landing on the whiteboard,
  // but only for first-time users. Subsequent loads are silent.
  useEffect(() => {
    if (hasSeenExplainer || status !== VOICE_STATUS.IDLE) return;
    const timer = setTimeout(() => setShowExplainer(true), 800);
    return () => clearTimeout(timer);
  }, [hasSeenExplainer, status]);

  const dismissExplainer = () => {
    setShowExplainer(false);
    setHasSeenExplainer(true);
    try { localStorage.setItem(EXPLAINER_SEEN_KEY, '1'); } catch { /* ignore */ }
  };

  const handleJoinVoice = () => {
    dismissExplainer();
    joinVoice();
  };

  if (status === VOICE_STATUS.IDLE) {
    return (
      <>
        <div className="fixed bottom-3 right-3 z-20 flex flex-col items-end gap-2">
          {/* First-time explainer tooltip */}
          {showExplainer && (
            <div className="max-w-[240px] bg-white rounded-xl shadow-xl border border-slate-200 p-3 relative animate-[fadeIn_0.3s_ease-out]">
              <button
                onClick={dismissExplainer}
                className="absolute top-1.5 right-1.5 w-5 h-5 flex items-center justify-center rounded text-slate-300 hover:text-charcoal hover:bg-slate-100 transition-colors"
                aria-label="Dismiss"
              >
                <X className="w-3 h-3" />
              </button>
              <p className="text-xs font-mono text-amber uppercase tracking-wider mb-1 pr-4">
                Voice chat
              </p>
              <p className="text-xs text-charcoal font-display leading-relaxed mb-2">
                Tap the phone to join the voice channel. You'll hear anyone else who's also joined — no ringing, no waiting.
              </p>
              <p className="text-[11px] text-slate-400 font-display italic leading-snug">
                Think of it like walking into the workshop and starting to chat.
              </p>
              {/* Arrow pointing down to the button */}
              <div className="absolute -bottom-1.5 right-5 w-3 h-3 bg-white border-r border-b border-slate-200 transform rotate-45" />
            </div>
          )}

          <button
            onClick={handleJoinVoice}
            title="Join voice chat — talk with anyone else in voice"
            aria-label="Join voice chat"
            className={`w-11 h-11 flex items-center justify-center rounded-full bg-charcoal/80 backdrop-blur text-white/80 hover:text-white hover:bg-charcoal transition-all shadow-lg ${
              showExplainer ? 'ring-2 ring-amber ring-offset-2 ring-offset-canvas animate-pulse' : ''
            }`}
          >
            <Phone className="w-5 h-5" />
          </button>
        </div>
      </>
    );
  }

  return (
    <>
      {/* Hidden audio elements — one per remote peer */}
      {[...remoteStreams.entries()].map(([peerId, stream]) => (
        <RemoteAudio key={peerId} stream={stream} />
      ))}

      {/* Voice panel — floating bottom-right */}
      <div className="fixed bottom-3 right-3 z-20 flex flex-col items-end gap-2">
        {/* Peer list */}
        {isActive && peers.length > 0 && (
          <div className="bg-white/95 backdrop-blur rounded-xl shadow-xl border border-slate-100 p-2 min-w-[140px] max-w-[200px]">
            <div className="px-2 py-1 flex items-center justify-between">
              <span className="text-[10px] font-mono text-slate-400 uppercase tracking-wider">
                In voice
              </span>
              <span className="text-[10px] font-mono text-amber">
                {peers.length + 1}
              </span>
            </div>
            <div className="space-y-0.5">
              <PeerRow nickname="You" isMuted={isMuted} isSelf />
              {peers.map((peer) => (
                <PeerRow
                  key={peer.userId}
                  nickname={peer.nickname}
                  stream={remoteStreams.get(peer.userId)}
                />
              ))}
            </div>
          </div>
        )}

        {/* Error display */}
        {status === VOICE_STATUS.ERROR && error && (
          <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2 max-w-[220px]">
            <p className="text-xs text-red-600 font-display">
              Voice error: {error.message || 'Failed to join'}
            </p>
          </div>
        )}

        {/* Controls */}
        <div className="flex items-center gap-2">
          {/* Mute toggle */}
          {isActive && (
            <button
              onClick={toggleMute}
              title={isMuted ? 'Unmute' : 'Mute'}
              className={`w-10 h-10 flex items-center justify-center rounded-full backdrop-blur transition-colors shadow-lg ${
                isMuted
                  ? 'bg-red-500 text-white hover:bg-red-600'
                  : 'bg-charcoal/80 text-white hover:bg-charcoal'
              }`}
            >
              {isMuted ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
            </button>
          )}

          {/* Leave voice */}
          <button
            onClick={leaveVoice}
            title="Leave voice chat"
            className="w-11 h-11 flex items-center justify-center rounded-full bg-red-500 text-white hover:bg-red-600 transition-colors shadow-lg"
          >
            {status === VOICE_STATUS.CONNECTING ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <PhoneOff className="w-5 h-5" />
            )}
          </button>
        </div>
      </div>
    </>
  );
}

// Row in the peer list with speaking indicator
function PeerRow({ nickname, stream, isMuted, isSelf }) {
  const isSpeaking = useIsSpeaking(stream);

  return (
    <div className="flex items-center gap-2 px-2 py-1 rounded-lg">
      <div
        className={`w-6 h-6 rounded-full flex items-center justify-center transition-all ${
          isSpeaking
            ? 'bg-emerald-500 ring-2 ring-emerald-500/30 scale-110'
            : 'bg-slate-200'
        }`}
      >
        {isMuted ? (
          <MicOff className="w-3 h-3 text-slate-500" />
        ) : (
          <Mic className={`w-3 h-3 ${isSpeaking ? 'text-white' : 'text-slate-500'}`} />
        )}
      </div>
      <span className="text-xs font-display text-charcoal truncate flex-1">
        {nickname}
        {isSelf && <span className="text-[9px] text-slate-400 font-mono ml-1">you</span>}
      </span>
    </div>
  );
}

// Hidden audio element for a remote stream
function RemoteAudio({ stream }) {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current && stream) {
      ref.current.srcObject = stream;
      ref.current.play().catch(() => {
        // Autoplay may be blocked — user interaction will fix it
      });
    }
  }, [stream]);
  return <audio ref={ref} autoPlay playsInline style={{ display: 'none' }} />;
}

// Simple volume-based speaking detection using Web Audio API
function useIsSpeaking(stream) {
  const [speaking, setSpeaking] = useState(false);

  useEffect(() => {
    if (!stream) {
      setSpeaking(false);
      return;
    }

    let audioContext;
    let analyser;
    let source;
    let rafId;
    let cancelled = false;

    try {
      audioContext = new (window.AudioContext || window.webkitAudioContext)();
      source = audioContext.createMediaStreamSource(stream);
      analyser = audioContext.createAnalyser();
      analyser.fftSize = 256;
      analyser.smoothingTimeConstant = 0.5;
      source.connect(analyser);

      const dataArray = new Uint8Array(analyser.frequencyBinCount);

      const tick = () => {
        if (cancelled) return;
        analyser.getByteFrequencyData(dataArray);
        const avg = dataArray.reduce((a, b) => a + b, 0) / dataArray.length;
        setSpeaking(avg > 15); // threshold — tuned empirically
        rafId = requestAnimationFrame(tick);
      };
      tick();
    } catch (err) {
      console.warn('[useIsSpeaking] Failed:', err);
    }

    return () => {
      cancelled = true;
      if (rafId) cancelAnimationFrame(rafId);
      try { source?.disconnect(); } catch { /* ignore */ }
      try { audioContext?.close(); } catch { /* ignore */ }
    };
  }, [stream]);

  return speaking;
}
